import { PaginationControls } from "@/components/pagination-controls";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import {
  useCancelInvitation,
  useDeleteUser,
  useInternalUsers,
  useInviteInternalUser,
  useRemoveUserRole,
  useRequestPasswordReset,
  useResendInvitation,
  useRestoreUser,
  useSuspendUser,
  useUpdateUserRole,
  type InternalUser,
} from "@/hooks/useInternalUsers";
import { useAuth } from "@/lib/auth";
import {
  getPrimaryRole,
  roleDescriptions,
  roleDisplayNames,
} from "@/lib/permissions";
import { AdminRole } from "@/lib/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Edit,
  Key,
  Mail,
  MoreVertical,
  RefreshCw,
  Search,
  Send,
  Shield,
  ShieldMinus,
  Trash2,
  UserCog,
  UserPlus,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { nameField } from "@/lib/validation";

const inviteUserSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  displayName: nameField(2, "Display name"),
  role: z.enum(["admin", "coach_manager"]),
});

const editUserSchema = z.object({
  role: z.enum(["admin", "coach_manager"]),
});

type InviteUserFormData = z.infer<typeof inviteUserSchema>;

// The role this dialog can show and change: the server only grants admin or
// coach manager, so other roles (influencer, coach, super admin) are read-only.
function editableRoleOf(roles: string[]): EditUserFormData["role"] | undefined {
  if (roles.includes("admin")) return "admin";
  if (roles.includes("coach_manager")) return "coach_manager";
  return undefined;
}
type EditUserFormData = z.infer<typeof editUserSchema>;

function getRoleIcon(role: AdminRole) {
  switch (role) {
    case "super_admin":
      return <Shield className="h-4 w-4" />;
    case "admin":
      return <UserCog className="h-4 w-4" />;
    case "coach_manager":
      return <UserCog className="h-4 w-4" />;
    default:
      return <UserCog className="h-4 w-4" />;
  }
}

function getRoleBadgeVariant(
  role: AdminRole,
): "default" | "secondary" | "outline" {
  switch (role) {
    case "super_admin":
      return "default";
    case "admin":
      return "secondary";
    default:
      return "outline";
  }
}

function formatLastLogin(dateString: string | null): string {
  if (!dateString) return "Never";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return "Just now";
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function InternalUsers() {
  const { user, hasPermission } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<InternalUser | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: "suspend" | "delete" | "resetPassword" | "cancelInvite";
    user: InternalUser;
  } | null>(null);
  const [activeTab, setActiveTab] = useState("active");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const canManageUsers = hasPermission("users", "manage");

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, roleFilter, activeTab]);

  const { data: usersResponse, isLoading } = useInternalUsers({
    page: currentPage,
    limit: pageSize,
    role: roleFilter === "all" ? undefined : roleFilter,
    status: activeTab,
    search: debouncedSearchQuery ? debouncedSearchQuery : undefined,
  });

  const users = usersResponse?.users ?? [];
  const statusCounts = usersResponse?.statusCounts ?? {
    active: 0,
    pending: 0,
    suspended: 0,
  };

  const inviteMutation = useInviteInternalUser();
  const suspendMutation = useSuspendUser();
  const restoreMutation = useRestoreUser();
  const deleteMutation = useDeleteUser();
  const resendInvitationMutation = useResendInvitation();
  const cancelInvitationMutation = useCancelInvitation();
  const requestPasswordResetMutation = useRequestPasswordReset();
  const updateUserRoleMutation = useUpdateUserRole();
  const removeUserRoleMutation = useRemoveUserRole();
  const [removeRoleTarget, setRemoveRoleTarget] = useState<{
    userId: string;
    userName: string;
    role: string;
  } | null>(null);

  const handleRemoveRole = async () => {
    if (!removeRoleTarget) return;
    try {
      await removeUserRoleMutation.mutateAsync({
        userId: removeRoleTarget.userId,
        data: { role: removeRoleTarget.role as any },
      });
      setRemoveRoleTarget(null);
    } catch (error) {
      console.error("Failed to remove role:", error);
    }
  };

  const inviteForm = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: {
      email: "",
      displayName: "",
      role: "admin" as const,
    },
  });

  const editForm = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      role: "admin",
    },
  });

  const pageItems = users;

  const handleInvite = async (data: InviteUserFormData) => {
    try {
      await inviteMutation.mutateAsync({
        name: data.displayName,
        email: data.email,
        role: data.role,
      });

      toast({
        title: "Invitation sent",
        description: `An invitation has been sent to ${data.email}`,
      });
      setIsInviteDialogOpen(false);
      inviteForm.reset();
    } catch (error: any) {
      const message =
        error?.message ||
        "An error occurred while sending the invitation. Please try again.";
      toast({
        title: "Failed to send invitation",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleOpenEditDialog = (internalUser: InternalUser) => {
    setEditingUser(internalUser);
    editForm.reset({
      role: editableRoleOf(internalUser.roles ?? []) ?? "admin",
    });
    setIsEditDialogOpen(true);
  };

  const handleEditUser = async (data: EditUserFormData) => {
    if (!editingUser) return;

    try {
      const roleChanged = !(editingUser.roles ?? []).includes(data.role);

      if (roleChanged) {
        await updateUserRoleMutation.mutateAsync({
          userId: editingUser.id,
          data: {
            role: data.role,
          },
        });
        // success / error toast is handled inside useUpdateUserRole
      } else {
        toast({
          title: "No changes made",
          description: "No changes were detected",
          variant: "default",
        });
      }

      setIsEditDialogOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error("Failed to update user role:", error);
    }
  };

  const handleSuspendUser = async (userId: string) => {
    try {
      await suspendMutation.mutateAsync({ userId });
    } catch (error) {
      console.error("Failed to suspend user:", error);
    }
  };

  const handleRestoreUser = async (userId: string) => {
    try {
      await restoreMutation.mutateAsync({ userId });
    } catch (error) {
      console.error("Failed to restore user:", error);
    }
  };

  const handleResetPassword = (email: string) => {
    // Hook handles success/error toasts itself; no need to duplicate.
    requestPasswordResetMutation.mutate({ email });
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, user: targetUser } = confirmAction;
    try {
      switch (type) {
        case "suspend":
          await suspendMutation.mutateAsync({ userId: targetUser.id });
          break;
        case "delete":
          await deleteMutation.mutateAsync({ userId: targetUser.id });
          break;
        case "resetPassword":
          handleResetPassword(targetUser.email);
          break;
        case "cancelInvite":
          await cancelInvitationMutation.mutateAsync({
            invitationId: targetUser.id,
          });
          break;
      }
    } catch (error) {
      console.error(`Failed to ${type}:`, error);
    } finally {
      setConfirmAction(null);
    }
  };

  const handleResendInvite = async (invitationId: string) => {
    try {
      await resendInvitationMutation.mutateAsync({ invitationId });
    } catch (error) {
      console.error("Failed to resend invitation:", error);
    }
  };

  const handleCancelInvite = async (invitationId: string) => {
    try {
      await cancelInvitationMutation.mutateAsync({ invitationId });
    } catch (error) {
      console.error("Failed to cancel invitation:", error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await deleteMutation.mutateAsync({ userId });
    } catch (error) {
      console.error("Failed to delete user:", error);
    }
  };

  const stats = {
    total: statusCounts.active + statusCounts.pending + statusCounts.suspended,
    active: statusCounts.active,
    pending: statusCounts.pending,
    suspended: statusCounts.suspended,
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Internal Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage administrator and staff access to the portal
          </p>
        </div>
        {canManageUsers && (
          <Button
            onClick={() => setIsInviteDialogOpen(true)}
            data-testid="button-invite-user"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Invite User
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div
              className="text-2xl font-semibold"
              data-testid="stat-total-users"
            >
              {stats.total}
            </div>
            <div className="text-sm text-muted-foreground">Total Users</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div
              className="text-2xl font-semibold text-green-600"
              data-testid="stat-active-users"
            >
              {stats.active}
            </div>
            <div className="text-sm text-muted-foreground">Active</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div
              className="text-2xl font-semibold text-blue-600"
              data-testid="stat-pending-users"
            >
              {stats.pending}
            </div>
            <div className="text-sm text-muted-foreground">Pending Invites</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div
              className="text-2xl font-semibold text-amber-600"
              data-testid="stat-suspended-users"
            >
              {stats.suspended}
            </div>
            <div className="text-sm text-muted-foreground">Suspended</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle>Portal Users</CardTitle>
              <CardDescription>
                Users with access to this administration portal
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64"
                  data-testid="input-search-users"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger
                  className="w-40"
                  data-testid="select-role-filter"
                >
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="option-role-all">
                    All Roles
                  </SelectItem>
                  <SelectItem
                    value="super_admin"
                    data-testid="option-role-super-admin"
                  >
                    Super Admin
                  </SelectItem>
                  <SelectItem value="admin" data-testid="option-role-admin">
                    Admin
                  </SelectItem>
                  <SelectItem
                    value="coach_manager"
                    data-testid="option-role-coach-manager"
                  >
                    Coach Manager
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="mb-4">
              <TabsTrigger value="active" data-testid="tab-active">
                Active ({stats.active})
              </TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending">
                Pending ({stats.pending})
              </TabsTrigger>
              <TabsTrigger value="suspended" data-testid="tab-suspended">
                Suspended ({stats.suspended})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="space-y-2">
              {pageItems.map((internalUser) => (
                <UserRow
                  key={internalUser.id}
                  user={internalUser}
                  currentUserId={user?.id}
                  canManage={canManageUsers}
                  onEdit={() => handleOpenEditDialog(internalUser)}
                  onResetPassword={() =>
                    setConfirmAction({
                      type: "resetPassword",
                      user: internalUser,
                    })
                  }
                  onSuspend={() =>
                    setConfirmAction({ type: "suspend", user: internalUser })
                  }
                  onDelete={() =>
                    setConfirmAction({ type: "delete", user: internalUser })
                  }
                  onRemoveRole={(role) =>
                    setRemoveRoleTarget({
                      userId: internalUser.id,
                      userName: internalUser.displayName,
                      role,
                    })
                  }
                  requestPasswordResetMutation={requestPasswordResetMutation}
                />
              ))}
              {!isLoading && pageItems.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No active users found
                </div>
              )}
            </TabsContent>

            <TabsContent value="pending" className="space-y-2">
              {pageItems.map((internalUser) => (
                <PendingUserRow
                  key={internalUser.id}
                  user={internalUser}
                  canManage={canManageUsers}
                  onResendInvite={() => handleResendInvite(internalUser.id)}
                  onCancelInvite={() =>
                    setConfirmAction({
                      type: "cancelInvite",
                      user: internalUser,
                    })
                  }
                  isResending={resendInvitationMutation.isPending}
                />
              ))}
              {!isLoading && pageItems.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No pending invitations
                </div>
              )}
            </TabsContent>

            <TabsContent value="suspended" className="space-y-2">
              {pageItems.map((internalUser) => (
                <SuspendedUserRow
                  key={internalUser.id}
                  user={internalUser}
                  canManage={canManageUsers}
                  onRestore={() => handleRestoreUser(internalUser.id)}
                  onDelete={() =>
                    setConfirmAction({ type: "delete", user: internalUser })
                  }
                  isRestoring={restoreMutation.isPending}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
              {!isLoading && pageItems.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No suspended users
                </div>
              )}
            </TabsContent>

            {usersResponse?.pagination && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={usersResponse.pagination.totalPages}
                totalItems={usersResponse.pagination.total}
                startIndex={(currentPage - 1) * pageSize}
                endIndex={Math.min(
                  currentPage * pageSize,
                  usersResponse.pagination.total,
                )}
                canGoPrev={currentPage > 1}
                canGoNext={currentPage < usersResponse.pagination.totalPages}
                onPageChange={setCurrentPage}
                onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                onNext={() =>
                  setCurrentPage((prev) =>
                    Math.min(usersResponse.pagination.totalPages, prev + 1),
                  )
                }
              />
            )}
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite New User</DialogTitle>
            <DialogDescription>
              Send an invitation to add a new administrator or staff member to
              the portal.
            </DialogDescription>
          </DialogHeader>
          <Form {...inviteForm}>
            <form
              onSubmit={inviteForm.handleSubmit(handleInvite)}
              className="space-y-4"
            >
              <FormField
                control={inviteForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="user@evooluir.com"
                        data-testid="input-invite-email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={inviteForm.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Full name"
                        data-testid="input-invite-name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={inviteForm.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-invite-role">
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem
                          value="admin"
                          data-testid="option-invite-admin"
                        >
                          Admin
                        </SelectItem>
                        <SelectItem
                          value="coach_manager"
                          data-testid="option-invite-coach-manager"
                        >
                          Coach Manager
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {
                        roleDescriptions[
                          field.value as "admin" | "coach_manager"
                        ]
                      }
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsInviteDialogOpen(false)}
                  data-testid="button-cancel-invite"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={inviteMutation.isPending}
                  data-testid="button-send-invite"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {inviteMutation.isPending ? "Sending..." : "Send Invitation"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details and access level
            </DialogDescription>
          </DialogHeader>
          <Form {...editForm}>
            <form
              onSubmit={editForm.handleSubmit(handleEditUser)}
              className="space-y-4"
            >
              <div className="space-y-2">
                <FormLabel>Display Name</FormLabel>
                <div
                  className="p-3 rounded-md bg-muted text-sm"
                  data-testid="text-edit-name"
                >
                  {editingUser?.displayName ?? "—"}
                </div>
              </div>
              <div className="space-y-2">
                <FormLabel>Email Address</FormLabel>
                <div
                  className="p-3 rounded-md bg-muted text-sm"
                  data-testid="text-edit-email"
                >
                  {editingUser?.email ?? "—"}
                </div>
              </div>
              {editingUser && !editableRoleOf(editingUser.roles ?? []) ? (
                // EVO-101: show the real role instead of pre-filling "Admin".
                <div className="space-y-2">
                  <FormLabel>Role</FormLabel>
                  <div
                    className="flex items-center gap-2 p-3 rounded-md bg-muted"
                    data-testid="text-edit-role"
                  >
                    <Shield className="h-4 w-4 text-primary" />
                    <span className="font-medium">
                      {roleDisplayNames[
                        getPrimaryRole(editingUser.roles ?? []) as AdminRole
                      ] ?? "—"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {editingUser.roles?.includes("super_admin")
                      ? "Super Admin role cannot be changed"
                      : "This role can't be changed here"}
                  </p>
                </div>
              ) : (
                <FormField
                  control={editForm.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-edit-role">
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem
                            value="admin"
                            data-testid="option-edit-admin"
                          >
                            Admin
                          </SelectItem>
                          <SelectItem
                            value="coach_manager"
                            data-testid="option-edit-coach-manager"
                          >
                            Coach Manager
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        {
                          roleDescriptions[
                            field.value as "admin" | "coach_manager"
                          ]
                        }
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {editingUser &&
                editableRoleOf(editingUser.roles ?? []) &&
                !(editingUser.roles ?? []).includes(editForm.watch("role")) && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                    <div className="text-sm text-amber-800 dark:text-amber-200">
                      <p className="font-medium">Role change warning</p>
                      <p className="text-xs mt-1">
                        Changing this user's role from{" "}
                        {
                          roleDisplayNames[
                            editableRoleOf(editingUser.roles ?? []) ?? "admin"
                          ]
                        }{" "}
                        to{" "}
                        {
                          roleDisplayNames[
                            editForm.watch("role") as "admin" | "coach_manager"
                          ]
                        }{" "}
                        will immediately update their access permissions.
                      </p>
                    </div>
                  </div>
                )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                  data-testid="button-cancel-edit"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    updateUserRoleMutation.isPending ||
                    !editForm.formState.isDirty
                  }
                  data-testid="button-save-edit"
                >
                  {updateUserRoleMutation.isPending
                    ? "Saving..."
                    : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!confirmAction}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.type === "suspend" && "Suspend User"}
              {confirmAction?.type === "delete" && "Remove User"}
              {confirmAction?.type === "resetPassword" &&
                "Send Password Reset Email"}
              {confirmAction?.type === "cancelInvite" && "Cancel Invitation"}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.type === "suspend" &&
                `This will suspend ${confirmAction.user.displayName}'s account. They will not be able to sign in to the portal.`}
              {confirmAction?.type === "delete" &&
                `This will remove ${confirmAction.user.displayName} from the portal. The account will be hidden but data is preserved.`}
              {confirmAction?.type === "resetPassword" &&
                `A password reset email will be sent to ${confirmAction.user.email}. You will not see the new password.`}
              {confirmAction?.type === "cancelInvite" &&
                `This will cancel the pending invitation to ${confirmAction.user.email}. The recipient will no longer be able to accept it.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmAction(null)}
              disabled={
                suspendMutation.isPending ||
                deleteMutation.isPending ||
                requestPasswordResetMutation.isPending ||
                cancelInvitationMutation.isPending
              }
            >
              Cancel
            </Button>
            <Button
              variant={
                confirmAction?.type === "resetPassword"
                  ? "default"
                  : "destructive"
              }
              onClick={handleConfirmAction}
              disabled={
                suspendMutation.isPending ||
                deleteMutation.isPending ||
                requestPasswordResetMutation.isPending ||
                cancelInvitationMutation.isPending
              }
              data-testid="button-confirm-internal-action"
            >
              {suspendMutation.isPending ||
              deleteMutation.isPending ||
              requestPasswordResetMutation.isPending ||
              cancelInvitationMutation.isPending
                ? "Processing..."
                : confirmAction?.type === "suspend"
                  ? "Suspend"
                  : confirmAction?.type === "delete"
                    ? "Remove"
                    : confirmAction?.type === "resetPassword"
                      ? "Send Email"
                      : "Cancel Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!removeRoleTarget}
        onOpenChange={(open) => !open && setRemoveRoleTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove the{" "}
              <strong>
                {removeRoleTarget
                  ? roleDisplayNames[removeRoleTarget.role as AdminRole]
                  : ""}
              </strong>{" "}
              role from <strong>{removeRoleTarget?.userName}</strong>? Their
              associated profile will be deactivated but not deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveRoleTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveRole}
              disabled={removeUserRoleMutation.isPending}
            >
              <ShieldMinus className="h-4 w-4 mr-2" />
              {removeUserRoleMutation.isPending ? "Removing..." : "Remove Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface UserRowProps {
  user: InternalUser;
  currentUserId?: string;
  canManage: boolean;
  onEdit: () => void;
  onResetPassword: () => void;
  onSuspend: () => void;
  onDelete: () => void;
  onRemoveRole: (role: string) => void;
  requestPasswordResetMutation: any;
}

function UserRow({
  user: internalUser,
  currentUserId,
  canManage,
  onEdit,
  onResetPassword,
  onSuspend,
  onDelete,
  onRemoveRole,
  requestPasswordResetMutation,
}: UserRowProps) {
  return (
    <div
      className="flex items-center justify-between p-4 rounded-lg border bg-card hover-elevate"
      data-testid={`row-user-${internalUser.id}`}
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="text-sm font-medium text-primary">
            {internalUser.displayName.charAt(0)}
          </span>
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="font-medium"
              data-testid={`text-name-${internalUser.id}`}
            >
              {internalUser.displayName}
            </span>
            {(internalUser.roles ?? []).map((role) => (
              <Badge
                key={role}
                variant={getRoleBadgeVariant(role as AdminRole)}
                className="gap-1"
              >
                {getRoleIcon(role as AdminRole)}
                {roleDisplayNames[role as AdminRole] ?? role}
              </Badge>
            ))}
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
            <span className="flex items-center gap-1">
              <Mail className="h-3 w-3" />
              {internalUser.email}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Last login: {formatLastLogin(internalUser.lastLogin)}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {canManage &&
          internalUser.id !== currentUserId &&
          !internalUser.roles?.includes("super_admin") && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
                data-testid={`button-edit-${internalUser.id}`}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid={`button-actions-${internalUser.id}`}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={onResetPassword}
                    data-testid={`action-reset-${internalUser.id}`}
                  >
                    <Key className="h-4 w-4 mr-2" />
                    {requestPasswordResetMutation.isPending
                      ? "Sending..."
                      : "Reset Password"}
                  </DropdownMenuItem>
                  {(internalUser.roles ?? []).length > 1 && (
                    <>
                      <DropdownMenuSeparator />
                      {(internalUser.roles ?? [])
                        .filter((r) => r !== "super_admin")
                        .map((role) => (
                          <DropdownMenuItem
                            key={role}
                            onClick={() => onRemoveRole(role)}
                            className="text-amber-600"
                            data-testid={`action-remove-role-${role}-${internalUser.id}`}
                          >
                            <ShieldMinus className="h-4 w-4 mr-2" />
                            Remove {roleDisplayNames[role as AdminRole] ??
                              role}{" "}
                            role
                          </DropdownMenuItem>
                        ))}
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={onSuspend}
                    className="text-amber-600"
                    data-testid={`action-suspend-${internalUser.id}`}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Suspend User
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={onDelete}
                    className="text-destructive"
                    data-testid={`action-delete-${internalUser.id}`}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove User
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
      </div>
    </div>
  );
}

interface PendingUserRowProps {
  user: InternalUser;
  canManage: boolean;
  onResendInvite: () => void;
  onCancelInvite: () => void;
  isResending?: boolean;
}

function PendingUserRow({
  user: internalUser,
  canManage,
  onResendInvite,
  onCancelInvite,
  isResending,
}: PendingUserRowProps) {
  // EVO-98: invites last 7 days; Resend renews an expired one.
  const expired =
    !!internalUser.expiresAt && new Date(internalUser.expiresAt) < new Date();
  return (
    <div
      className="flex items-center justify-between p-4 rounded-lg border bg-card hover-elevate"
      data-testid={`row-pending-${internalUser.id}`}
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="font-medium"
              data-testid={`text-name-${internalUser.id}`}
            >
              {internalUser.displayName}
            </span>
            {(internalUser.roles ?? []).map((role) => (
              <Badge
                key={role}
                variant={getRoleBadgeVariant(role as AdminRole)}
                className="gap-1"
              >
                {getRoleIcon(role as AdminRole)}
                {roleDisplayNames[role as AdminRole] ?? role}
              </Badge>
            ))}
            {expired ? (
              <Badge
                variant="outline"
                className="bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                data-testid={`badge-expired-${internalUser.id}`}
              >
                Expired
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800"
              >
                Pending
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
            <span className="flex items-center gap-1">
              <Mail className="h-3 w-3" />
              {internalUser.email}
            </span>
            {internalUser.invitedAt && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Invited: {formatDate(internalUser.invitedAt)}
              </span>
            )}
            {internalUser.invitedBy && (
              <span className="text-xs">by {internalUser.invitedBy}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {canManage && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={onResendInvite}
              disabled={isResending}
              data-testid={`button-resend-${internalUser.id}`}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${isResending ? "animate-spin" : ""}`}
              />
              {isResending ? "Sending..." : "Resend"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid={`button-actions-${internalUser.id}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={onCancelInvite}
                  className="text-destructive"
                  data-testid={`action-cancel-${internalUser.id}`}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Invitation
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
    </div>
  );
}

interface SuspendedUserRowProps {
  user: InternalUser;
  canManage: boolean;
  onRestore: () => void;
  onDelete: () => void;
  isRestoring?: boolean;
  isDeleting?: boolean;
}

function SuspendedUserRow({
  user: internalUser,
  canManage,
  onRestore,
  onDelete,
  isRestoring,
  isDeleting,
}: SuspendedUserRowProps) {
  return (
    <div
      className="flex items-center justify-between p-4 rounded-lg border bg-card opacity-75 hover-elevate"
      data-testid={`row-suspended-${internalUser.id}`}
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
          <XCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="font-medium"
              data-testid={`text-name-${internalUser.id}`}
            >
              {internalUser.displayName}
            </span>
            {(internalUser.roles ?? []).map((role) => (
              <Badge
                key={role}
                variant={getRoleBadgeVariant(role as AdminRole)}
                className="gap-1"
              >
                {getRoleIcon(role as AdminRole)}
                {roleDisplayNames[role as AdminRole] ?? role}
              </Badge>
            ))}
            <Badge variant="destructive">Suspended</Badge>
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
            <span className="flex items-center gap-1">
              <Mail className="h-3 w-3" />
              {internalUser.email}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Last login: {formatLastLogin(internalUser.lastLogin)}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {canManage && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={onRestore}
              disabled={isRestoring || isDeleting}
              data-testid={`button-restore-${internalUser.id}`}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {isRestoring ? "Restoring..." : "Restore"}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              disabled={isDeleting || isRestoring}
              data-testid={`button-delete-${internalUser.id}`}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
